import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './BlindReveal.css';

gsap.registerPlugin(ScrollTrigger);

const BLIND_COUNT = 10;

const images = [
  'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1920&q=80',
  'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&q=80',
  'https://images.unsplash.com/photo-1682687220742-aba13b6e50ba?w=1920&q=80',
];

const BlindReveal: React.FC = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const exp = experiments.find((e) => e.id === 'blind-reveal');

  useEffect(() => {
    let imgSwapped = false;

    const ctx = gsap.context(() => {
      const blindEls = gsap.utils.toArray<HTMLElement>('.br-blind');
      const blindImgs = gsap.utils.toArray<HTMLImageElement>('.br-blind-img');
      const bgLayers = gsap.utils.toArray<HTMLElement>('.br-bg');

      gsap.set(blindEls, { transformPerspective: 800 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: scrollRef.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.3,
          onUpdate: (self) => {
            // Swap blind images during hold2 when blinds are at -180 (invisible)
            // At -180, backface-visibility hides them, so swap is invisible to user
            if (self.progress > 0.50 && !imgSwapped) {
              imgSwapped = true;
              blindImgs.forEach((img) => { img.src = images[2]; });
            } else if (self.progress <= 0.50 && imgSwapped) {
              imgSwapped = false;
              blindImgs.forEach((img) => { img.src = images[0]; });
            }
          },
        },
      });

      // ── Hold image 0 ──
      tl.to({}, { duration: 0.5 });

      // ── Flip 1: 0° → -180° (blinds disappear, reveal bg image 1) ──
      tl.addLabel('flip1');
      tl.set(bgLayers[0], { autoAlpha: 1 }, 'flip1');
      blindEls.forEach((blind, i) => {
        tl.to(blind, {
          rotateY: -180,
          duration: 0.8,
          ease: 'power2.inOut',
        }, `flip1+=${i * 0.06}`);
      });

      // ── Hold image 1 (blinds invisible at -180°) ──
      tl.addLabel('hold1', '>');
      tl.to({}, { duration: 0.8 }, 'hold1');

      // ── Flip 2: -180° → -360° (blinds reappear covering bg with image 2) ──
      // bg stays as images[1] — blinds gradually cover it with images[2]
      tl.addLabel('flip2', '>');
      blindEls.forEach((blind, i) => {
        tl.to(blind, {
          rotateY: -360,
          duration: 0.8,
          ease: 'power2.inOut',
        }, `flip2+=${i * 0.06}`);
      });

      // ── Hold image 2 ──
      tl.to({}, { duration: 0.5 }, '>');
    }, wrapperRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={wrapperRef} className="br-wrapper">
      <div className="br-scanlines" />
      <ExperimentNav prompt={exp?.prompt} title={exp?.title} />

      <div className="br-fixed">
        {/* Background layers behind blinds */}
        <img src={images[1]} alt="" className="br-bg" style={{ visibility: 'hidden', opacity: 0 }} />
        <img src={images[2]} alt="" className="br-bg" style={{ visibility: 'hidden', opacity: 0 }} />

        {/* Single-face blind slats */}
        {Array.from({ length: BLIND_COUNT }, (_, b) => {
          const leftPercent = (b / BLIND_COUNT) * 100;
          const widthPercent = 100 / BLIND_COUNT + 0.5;

          return (
            <div
              key={b}
              className="br-blind"
              style={{
                left: `${leftPercent}%`,
                width: `${widthPercent}%`,
              }}
            >
              <img
                src={images[0]}
                alt=""
                className="br-blind-img"
                style={{ marginLeft: `-${leftPercent}vw` }}
              />
            </div>
          );
        })}
      </div>

      <div ref={scrollRef} className="br-scroll-track">
        <div className="br-spacer" />
        <div className="br-spacer" />
        <div className="br-spacer" />
        <div className="br-spacer" />
        <div className="br-spacer" />
        <div className="br-spacer" />
      </div>
    </div>
  );
};

export default BlindReveal;
